import { QueryClientProvider, type QueryClient } from '@tanstack/react-query';
import { RouterProvider } from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import { Loading } from './components/states';
import type { ApiClient } from './lib/api';
import { AuthProvider, useAuth } from './lib/auth';
import { I18nProvider } from './lib/i18n';
import { DisplayProvider } from './lib/theme';
import { createQueryClient } from './lib/query';
import { createAppRouter } from './router';

function RoutedApp({ queryClient }: { queryClient: QueryClient }) {
  const auth = useAuth();
  const [router] = useState(() => createAppRouter({ api: auth.api, queryClient }));
  const status = auth.state.status;

  // Les gardes de route relisent la session à chaque changement (connexion, perte de session).
  useEffect(() => {
    if (status === 'loading') return;
    if (status === 'anonymous') queryClient.clear();
    void router.invalidate();
  }, [status, queryClient, router]);

  if (status === 'loading') return <Loading />;
  return <RouterProvider router={router} />;
}

export function App({ api, queryClient }: { api: ApiClient; queryClient?: QueryClient }) {
  const [client] = useState(() => queryClient ?? createQueryClient());
  return (
    <I18nProvider>
      <DisplayProvider>
        <QueryClientProvider client={client}>
          <AuthProvider api={api}>
            <RoutedApp queryClient={client} />
          </AuthProvider>
        </QueryClientProvider>
      </DisplayProvider>
    </I18nProvider>
  );
}
